/**
 * Communities (SupaAdmin) and Teams (Admin) management views.
 *
 * Kept in a single file to avoid further bloating App.tsx. Agent fields
 * use a searchable name picker (DialpadAgentPicker) backed by the
 * synced dialpad_users list.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AssessmentTeamRef, AssignableAssessment } from "../lib/api";
import { setTeamAssessmentsWithRetry } from "../lib/setTeamAssessmentsWithRetry";
import {
  computeAgentsInOtherTeams,
  computeDuplicateAgentIds,
  computeEligibleAgents,
} from "./accessControlDuplicates";
import { useConfirmDialog } from "../components/ConfirmDialog.helpers";
import { TeamScenarioFields } from "./TeamScenarioFields";
import type { ScenarioSettingsUpdate } from "../lib/api";

import {
  api,
  type AdminScopedCommunity,
  type DialpadUserRow,
  type CommunityIn,
  type CommunityOut,
  type CommunityStatus,
  type TeamIn,
  type TeamOut,
  type UnassignedCall,
  type UserOut,
} from "../lib/api";

// Brands the org currently understands. Mirrors KNOWN_BRANDS in the API
// (sales_api/services/dialpad_users.py); kept inline because the list is
// short, fixed, and used by the brand picker on Community editor.
const KNOWN_BRANDS = ["petnsur", "broadlands", "autolife", "unknown"] as const;
const BRAND_LABELS: Record<string, string> = {
  petnsur: "Pet-N-Sur",
  broadlands: "Broadlands",
  autolife: "Autolife",
  unknown: "Unknown / unassigned",
};
function brandLabel(b: string): string {
  return BRAND_LABELS[b] ?? b;
}

// ---------------------------------------------------------------------------
// Reusable: searchable Dialpad agent picker (multi-select by name/email).
// Stored value is an array of dialpad_user_id strings. Saved IDs that no
// longer match a synced user are still rendered as removable chips with a
// warning marker, so existing data is never silently dropped on edit.
// ---------------------------------------------------------------------------

export function DialpadAgentPicker({
  value,
  onChange,
  options,
  excludeIds,
  duplicateIds,
  placeholder = "No agents selected",
}: {
  value: string[];
  onChange: (ids: string[]) => void;
  options: DialpadUserRow[];
  // Agents already on another team — never offered as add options, but
  // still rendered as chips if currently selected so they stay removable.
  excludeIds?: Set<string>;
  // Agents that belong to more than one team — flagged as destructive chips.
  duplicateIds?: Set<string>;
  placeholder?: string;
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);

  const optionMap = useMemo(
    () => new Map(options.map((o) => [o.id, o])),
    [options],
  );
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base =
      excludeIds && excludeIds.size > 0
        ? options.filter((o) => !excludeIds.has(o.id))
        : options;
    if (!q) return base;
    return base.filter(
      (o) =>
        (o.display_name || "").toLowerCase().includes(q) ||
        (o.email || "").toLowerCase().includes(q) ||
        o.id.includes(q),
    );
  }, [options, query, excludeIds]);

  const toggle = (id: string) => {
    onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  };
  const remove = (id: string) => onChange(value.filter((x) => x !== id));

  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap gap-1.5 min-h-[1.75rem]">
        {value.length === 0 ? (
          <span className="text-xs text-muted-foreground italic">{placeholder}</span>
        ) : (
          value.map((id) => {
            const o = optionMap.get(id);
            const label = o ? (o.display_name || o.email || id) : id;
            const isDuplicate = !!duplicateIds?.has(id);
            return (
              <span
                key={id}
                title={
                  isDuplicate
                    ? "This agent is also on another team — remove them here to fix the duplicate"
                    : undefined
                }
                className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${
                  isDuplicate
                    ? "border-destructive/30 bg-destructive/10 text-destructive"
                    : "bg-muted border-border"
                }`}
              >
                {!o && (
                  <span
                    title="Unknown agent — not in current Dialpad sync"
                    className="text-accent-foreground"
                  >
                    ⚠
                  </span>
                )}
                <span className={isDuplicate ? "text-destructive" : "text-foreground"}>
                  {label}
                </span>
                <button
                  type="button"
                  onClick={() => remove(id)}
                  className={
                    isDuplicate
                      ? "text-destructive hover:text-destructive/80"
                      : "text-muted-foreground hover:text-foreground"
                  }
                  aria-label={`Remove ${label}`}
                >
                  ×
                </button>
              </span>
            );
          })
        )}
      </div>
      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder="Search agents by name, email or ID…"
          className="w-full border border-input rounded px-2 py-1.5 text-sm"
        />
        {open && filtered.length > 0 && (
          <div className="absolute z-10 mt-1 w-full max-h-56 overflow-y-auto bg-card border border-input rounded shadow-lg">
            {filtered.slice(0, 100).map((o) => {
              const checked = value.includes(o.id);
              return (
                <button
                  type="button"
                  key={o.id}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => toggle(o.id)}
                  className={`w-full text-left px-2 py-1 text-sm flex items-center gap-2 hover:bg-muted/40 ${
                    checked ? "bg-muted/40" : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    readOnly
                    className="pointer-events-none"
                  />
                  <span className="flex-1 truncate">
                    <span className="font-medium text-foreground">
                      {o.display_name || o.email || o.id}
                    </span>
                    {o.display_name && o.email && (
                      <span className="text-muted-foreground"> · {o.email}</span>
                    )}
                  </span>
                </button>
              );
            })}
            {filtered.length > 100 && (
              <div className="px-2 py-1 text-xs text-muted-foreground border-t border-border">
                Showing 100 of {filtered.length} — refine your search
              </div>
            )}
          </div>
        )}
        {open && filtered.length === 0 && query.trim() !== "" && (
          <div className="absolute z-10 mt-1 w-full bg-card border border-input rounded shadow-lg px-2 py-2 text-xs text-muted-foreground">
            No matches in synced Dialpad users.
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SupaAdmin: Communities
// ---------------------------------------------------------------------------

export function CommunitiesView() {
  const qc = useQueryClient();
  const dialog = useConfirmDialog();
  const communitiesQ = useQuery<CommunityOut[]>({
    queryKey: ["communities"],
    queryFn: () => api.listCommunities(),
  });
  const usersQ = useQuery<UserOut[]>({
    queryKey: ["users"],
    queryFn: () => api.users(),
  });
  const dpUsersQ = useQuery({
    queryKey: ["dialpad-users"],
    queryFn: () => api.dialpadUsers(1000),
  });

  const adminCandidates = useMemo(
    () => (usersQ.data ?? []).filter((u) => u.role === "admin" || u.role === "supadmin"),
    [usersQ.data],
  );

  const [editing, setEditing] = useState<CommunityOut | "new" | null>(null);

  const createM = useMutation({
    mutationFn: (body: CommunityIn) => api.createCommunity(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["communities"] });
      setEditing(null);
    },
  });
  const updateM = useMutation({
    mutationFn: ({ id, body }: { id: string; body: CommunityIn }) =>
      api.updateCommunity(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["communities"] });
      setEditing(null);
    },
  });
  const deleteM = useMutation({
    mutationFn: (id: string) => api.deleteCommunity(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["communities"] }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Manage Communities</h2>
          <p className="text-sm text-muted-foreground">
            Define which Dialpad calls each Community covers, manage per-agent
            exceptions, and assign Admins.
          </p>
        </div>
        <button
          type="button"
          className="rounded bg-primary text-primary-foreground text-sm px-3 py-1.5 hover:bg-primary/90 transition-colors duration-[var(--duration-base)] ease-[var(--easing-standard)]"
          onClick={() => setEditing("new")}
        >
          New community
        </button>
      </div>

      {editing && (
        <CommunityEditor
          community={editing === "new" ? null : editing}
          allCommunities={communitiesQ.data ?? []}
          adminCandidates={adminCandidates}
          dialpadUsers={dpUsersQ.data?.rows ?? []}
          onCancel={() => setEditing(null)}
          onSubmit={(body) => {
            if (editing === "new") createM.mutate(body);
            else updateM.mutate({ id: editing.id, body });
          }}
          submitting={createM.isPending || updateM.isPending}
          error={(createM.error as { message?: string } | null)?.message
            || (updateM.error as { message?: string } | null)?.message}
        />
      )}

      <section className="rounded-lg border border-border bg-card shadow-md animate-card-enter">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/10 text-muted-foreground text-xs uppercase">
              <tr>
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Default</th>
                <th className="text-left px-3 py-2">Brands</th>
                <th className="text-left px-3 py-2">Agents</th>
                <th className="text-left px-3 py-2">Exceptions</th>
                <th className="text-left px-3 py-2">Admins</th>
                <th className="text-right px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {(communitiesQ.data ?? []).map((g) => (
                <tr key={g.id} className="border-t border-border hover:bg-primary/5 transition-colors duration-[var(--duration-base)] ease-[var(--easing-standard)]">
                  <td className="px-3 py-2 font-medium">{g.name}</td>
                  <td className="px-3 py-2">
                    <StatusPill status={g.status} />
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {g.is_default ? (
                      <span className="inline-flex rounded-full bg-secondary/15 text-secondary border border-secondary/30 px-2 py-0.5 text-xs font-medium">
                        Default
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {g.brands.length === 0 ? (
                      "—"
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {g.brands.map((b) => (
                          <span
                            key={b}
                            className="inline-flex rounded-full bg-muted border border-border px-2 py-0.5 text-xs text-foreground"
                          >
                            {brandLabel(b)}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-foreground tabular-nums">
                    {g.agent_count}
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {g.exceptions.length === 0
                      ? "—"
                      : `${g.exceptions.filter((e) => e.mode === "include").length} +, `
                        + `${g.exceptions.filter((e) => e.mode === "exclude").length} −`}
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {g.admin_user_ids.length}
                  </td>
                  <td className="px-3 py-2 text-right text-xs">
                    <button
                      type="button"
                      onClick={() => setEditing(g)}
                      className="text-foreground hover:underline mr-3"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={async () => {
                        const ok = await dialog.confirm({
                          title: "Delete community?",
                          message: `Delete community "${g.name}"?`,
                          confirmLabel: "Delete",
                          destructive: true,
                        });
                        if (ok) deleteM.mutate(g.id);
                      }}
                      className="text-destructive hover:underline"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
              {communitiesQ.isLoading ? (
                <tr>
                  <td colSpan={8} className="px-3 py-6 text-center text-sm text-muted-foreground">Loading…</td>
                </tr>
              ) : (communitiesQ.data?.length ?? 0) === 0 && (
                <tr>
                  <td colSpan={8} className="px-3 py-6 text-center text-sm text-muted-foreground">
                    No communities yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
      {dialog.element}
    </div>
  );
}

function StatusPill({ status }: { status: CommunityStatus }) {
  const cls = status === "active"
    ? "bg-primary/15 text-primary"
    : "bg-accent/20 text-accent-foreground";
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>
      {status}
    </span>
  );
}

function CommunityEditor({
  community,
  allCommunities,
  adminCandidates,
  dialpadUsers,
  onCancel,
  onSubmit,
  submitting,
  error,
}: {
  community: CommunityOut | null;
  allCommunities: CommunityOut[];
  adminCandidates: UserOut[];
  dialpadUsers: DialpadUserRow[];
  onCancel: () => void;
  onSubmit: (body: CommunityIn) => void;
  submitting: boolean;
  error?: string;
}) {
  const [name, setName] = useState(community?.name ?? "");
  const [status, setStatus] = useState<CommunityStatus>(community?.status ?? "draft");
  const [brands, setBrands] = useState<string[]>(community?.brands ?? []);
  const [includes, setIncludes] = useState<string[]>(
    (community?.exceptions ?? []).filter((e) => e.mode === "include").map((e) => e.dialpad_user_id),
  );
  const [excludes, setExcludes] = useState<string[]>(
    (community?.exceptions ?? []).filter((e) => e.mode === "exclude").map((e) => e.dialpad_user_id),
  );
  const [adminIds, setAdminIds] = useState<string[]>(community?.admin_user_ids ?? []);
  const [isDefault, setIsDefault] = useState<boolean>(community?.is_default ?? false);
  const currentDefault = allCommunities.find((c) => c.is_default) ?? null;
  const isCurrentlyDefault = !!community?.is_default;
  // While ≥1 community exists, the default flag must remain set on
  // exactly one row. The only way to "uncheck" it on the current
  // default is to check it on a different community.
  const cannotUncheck = isCurrentlyDefault;

  const toggleBrand = (b: string) => {
    setBrands((prev) =>
      prev.includes(b) ? prev.filter((x) => x !== b) : [...prev, b],
    );
  };

  const resolvedMembers = useMemo(() => {
    const brandSet = new Set(brands);
    const includeSet = new Set(includes);
    const excludeSet = new Set(excludes);
    const passes = (u: DialpadUserRow | null, id: string): boolean => {
      if (excludeSet.has(id)) return false;
      if (includeSet.has(id)) return true;
      const brand = u?.brand ?? null;
      return !!brand && brandSet.has(brand);
    };
    const seen = new Map<string, DialpadUserRow | null>();
    for (const u of dialpadUsers) {
      if (passes(u, u.id)) seen.set(u.id, u);
    }
    // Force-included agents whose Dialpad record isn't loaded — keep
    // as id-only rows so admins can see their override took effect.
    for (const id of includeSet) {
      if (!seen.has(id) && !excludeSet.has(id)) {
        seen.set(id, null);
      }
    }
    const rows = Array.from(seen.entries()).map(([id, u]) => ({
      id,
      name: (u?.display_name || "").trim(),
      email: (u?.email || "").trim(),
    }));
    rows.sort((a, b) => {
      const an = (a.name || a.email || a.id).toLowerCase();
      const bn = (b.name || b.email || b.id).toLowerCase();
      return an.localeCompare(bn);
    });
    return rows;
  }, [dialpadUsers, brands, includes, excludes]);

  const submit = () => {
    const body: CommunityIn = {
      name: name.trim(),
      status,
      brands,
      exceptions: [
        ...includes.map((id) => ({ dialpad_user_id: id, mode: "include" as const })),
        ...excludes.map((id) => ({ dialpad_user_id: id, mode: "exclude" as const })),
      ],
      admin_user_ids: adminIds,
      is_default: isDefault,
    };
    onSubmit(body);
  };

  return (
    <div className="rounded-lg border border-input bg-card p-4 shadow-md space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">{community ? `Edit community: ${community.name}` : "New community"}</h3>
        <button type="button" onClick={onCancel} className="text-sm text-muted-foreground hover:text-foreground">
          Cancel
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="text-xs text-muted-foreground">
          Name
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 w-full border border-input rounded px-2 py-1.5 text-sm"
          />
        </label>
        <label className="text-xs text-muted-foreground">
          Status
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as CommunityStatus)}
            className="mt-1 w-full border border-input rounded px-2 py-1.5 text-sm"
          >
            <option value="draft">Draft (hidden from Admins)</option>
            <option value="active">Active</option>
          </select>
        </label>
      </div>
      <div>
        <div className="text-xs text-muted-foreground mb-1">
          Brands in this community <span className="text-muted-foreground">— a call lands in this community when the agent's brand matches one of these</span>
        </div>
        <div className="flex flex-wrap gap-2 border border-border rounded p-2 bg-muted/40">
          {KNOWN_BRANDS.map((b) => {
            const checked = brands.includes(b);
            return (
              <label
                key={b}
                className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs cursor-pointer ${
                  checked
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-card text-foreground border-input hover:bg-muted"
                }`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleBrand(b)}
                  className="sr-only"
                />
                {brandLabel(b)}
              </label>
            );
          })}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="space-y-1">
          <div className="text-xs text-muted-foreground">
            Force-include agents (override filter)
          </div>
          <DialpadAgentPicker
            value={includes}
            onChange={setIncludes}
            options={dialpadUsers}
            // An agent force-excluded can't also be force-included (the
            // backend rejects the conflict; keep it un-pickable here so the
            // user never hits that 422).
            excludeIds={new Set(excludes)}
            placeholder="No agents force-included"
          />
        </div>
        <div className="space-y-1">
          <div className="text-xs text-muted-foreground">Force-exclude agents</div>
          <DialpadAgentPicker
            value={excludes}
            onChange={setExcludes}
            options={dialpadUsers}
            excludeIds={new Set(includes)}
            placeholder="No agents force-excluded"
          />
        </div>
      </div>
      <div className="rounded border border-border bg-muted/40 p-2 space-y-1">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={isDefault}
            disabled={cannotUncheck && isDefault}
            onChange={(e) => {
              if (cannotUncheck && !e.target.checked) return;
              setIsDefault(e.target.checked);
            }}
          />
          <span className="font-medium">Default community</span>
          <span className="text-xs text-muted-foreground">
            — new admins with no explicit community assignment land here
          </span>
        </label>
        {isDefault && !isCurrentlyDefault && currentDefault && (
          <div className="text-xs text-accent-foreground">
            This will replace <span className="font-medium">{currentDefault.name}</span> as the default.
          </div>
        )}
        {cannotUncheck && (
          <div className="text-xs text-muted-foreground">
            To change the default, check this box on a different community instead.
          </div>
        )}
      </div>
      <div>
        <div className="text-xs text-muted-foreground mb-1">Admins assigned to this community</div>
        <div className="max-h-40 overflow-y-auto border border-border rounded p-2 space-y-1">
          {adminCandidates.length === 0 ? (
            <div className="text-xs text-muted-foreground">No admins available.</div>
          ) : adminCandidates.map((u) => (
            <label key={u.id} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={adminIds.includes(u.id)}
                onChange={(e) => {
                  setAdminIds((prev) =>
                    e.target.checked ? [...prev, u.id] : prev.filter((x) => x !== u.id),
                  );
                }}
              />
              <span>{u.full_name || u.email}</span>
              <span className="text-xs text-muted-foreground">{u.role}</span>
            </label>
          ))}
        </div>
      </div>
      <details className="text-xs text-muted-foreground">
        <summary className="cursor-pointer">
          Show agents in this community ({resolvedMembers.length})
        </summary>
        <div className="mt-2 max-h-40 overflow-y-auto text-xs border border-border rounded">
          {resolvedMembers.length === 0 ? (
            <div className="px-2 py-1.5 italic text-muted-foreground">
              No agents match this selection yet.
            </div>
          ) : (
            resolvedMembers.map((m) => (
              <div
                key={m.id}
                className="px-2 py-1 border-b border-border last:border-b-0"
              >
                <span className="text-foreground">
                  {m.name || m.email || m.id}
                </span>
                {m.name && m.email && (
                  <span className="text-muted-foreground"> · {m.email}</span>
                )}
              </div>
            ))
          )}
        </div>
      </details>
      {error && <div className="text-xs text-destructive">{error}</div>}
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={submit}
          disabled={submitting || !name.trim()}
          className="rounded bg-primary text-primary-foreground text-sm px-3 py-1.5 hover:bg-primary/90 disabled:opacity-50 transition-colors duration-[var(--duration-base)] ease-[var(--easing-standard)]"
        >
          {submitting ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Admin: Teams
// ---------------------------------------------------------------------------

// Availability is read-only in the list; policy and activation are saved
// together in the team's revision-protected editor.
function ScenarioAvailability({ team }: { team: TeamOut }) {
  return (
    <span aria-label={`Scenarios for ${team.name}: ${team.scenarios_enabled ? "active" : "inactive"}`}>
      {team.scenarios_enabled ? "Active" : "Inactive"}
    </span>
  );
}

export function TeamsView({ me }: { me: UserOut }) {
  const qc = useQueryClient();
  const dialog = useConfirmDialog();
  const isSupadmin = me.role === "supadmin";
  const teamsQ = useQuery<TeamOut[]>({
    queryKey: ["teams"],
    queryFn: () => api.listTeams(),
  });
  const scopeQ = useQuery<AdminScopedCommunity[]>({
    queryKey: ["scoped-communities"],
    queryFn: () => api.scopedCommunities(),
  });
  const usersQ = useQuery<UserOut[]>({
    queryKey: ["users"],
    queryFn: () => api.users(),
  });
  const dpUsersQ = useQuery({
    queryKey: ["dialpad-users"],
    queryFn: () => api.dialpadUsers(1000),
  });

  const managerCandidates = useMemo(
    () => (usersQ.data ?? []).filter((u) => u.role === "manager"),
    [usersQ.data],
  );

  const [editing, setEditing] = useState<TeamOut | "new" | null>(null);
  // When the admin clicks the Assessments cell/column on a row, open the
  // editor and ask it to scroll its Assessments section into view.
  const [scrollToAssessments, setScrollToAssessments] = useState(false);
  const openEditor = (s: TeamOut, focusAssessments = false) => {
    setScrollToAssessments(focusAssessments);
    setEditing(s);
  };

  // An agent should only ever belong to one team. From the teams we can
  // see, derive the set of agent ids that appear in more than one team
  // (duplicates), so we can flag them in the list and the editor. We
  // de-dupe within each team first so a single team that lists an agent
  // twice never reads as a cross-team duplicate.
  const duplicateAgentIds = useMemo(
    () => computeDuplicateAgentIds(teamsQ.data ?? []),
    [teamsQ.data],
  );

  // Agents that belong to a team *other* than the one currently open in
  // the editor. The picker uses this to stop offering anyone who is
  // already on another team (keyed off the open editor so the team's own
  // members aren't excluded from its own dropdown).
  const agentsInOtherTeams = useMemo(() => {
    const editingId = editing && editing !== "new" ? editing.id : null;
    return computeAgentsInOtherTeams(teamsQ.data ?? [], editingId);
  }, [teamsQ.data, editing]);

  const teamHasDuplicateAgent = (t: TeamOut) =>
    t.dialpad_user_ids.some((id) => duplicateAgentIds.has(id));
  // Sticky error shown after a team was successfully created but the
  // follow-up assessments PUT failed. We swap the editor into "edit
  // existing team" mode so the next Save retries only the assessments
  // call (task #34); this state survives that swap (and the createM
  // reset that goes with it) until the user retries or cancels.
  const [postCreateAssessmentError, setPostCreateAssessmentError] = useState<
    string | null
  >(null);

  // Two-step create when the admin pre-picks assessments: POST /teams
  // first so we own a team id, then PUT /teams/{id}/assessments. If
  // the second call fails we keep the editor open on the freshly
  // created team (now via the update path) so the next Save retries
  // just the assessments PUT — no duplicate team create. The user's
  // typed-in name/agents/managers/assessment picks survive because
  // TeamEditor stays mounted across the "new" → TeamOut swap.
  const createM = useMutation({
    mutationFn: async ({
      body,
      assessmentChange,
    }: {
      body: TeamIn;
      assessmentChange: { ids: string[] } | null;
    }) => {
      const team = await api.createTeam(body);
      if (assessmentChange) {
        try {
          await setTeamAssessmentsWithRetry(
            team.id,
            assessmentChange.ids,
          );
        } catch (err) {
          const msg = (err as { message?: string } | null)?.message
            || "Assessments could not be assigned";
          const wrapped = new Error(
            `Team "${team.name}" was created, but assigning assessments failed: ${msg}. Click Save to retry just the assessments.`,
          ) as Error & { createdTeam?: TeamOut };
          wrapped.createdTeam = team;
          throw wrapped;
        }
      }
      return team;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["teams"] });
      if (vars.assessmentChange) {
        qc.invalidateQueries({ queryKey: ["assessments"] });
      }
      setEditing(null);
      setScrollToAssessments(false);
    },
    // Whether the assessments PUT failed or the team create itself
    // failed, we still need the Teams list to reflect any partial
    // success on the next render.
    onError: (err) => {
      qc.invalidateQueries({ queryKey: ["teams"] });
      const createdTeam = (err as Error & { createdTeam?: TeamOut })
        .createdTeam;
      if (createdTeam) {
        // Stash the message before we reset the mutation (which would
        // otherwise wipe createM.error and leave the editor with no
        // explanation of what just went wrong).
        setPostCreateAssessmentError(err.message);
        setEditing(createdTeam);
        createM.reset();
      }
    },
  });
  // Sequential save: team PUT first (so we know the rename/agent
  // changes landed), then — only if the assessments selection actually
  // changed — the assessments PUT. Matches the pattern CommunityForm
  // uses for its child data (~line 467) and avoids two races sharing
  // the same toast lane.
  const updateM = useMutation({
    mutationFn: async ({
      id,
      body,
      assessmentChange,
      scenarioChange,
    }: {
      id: string;
      body: TeamIn;
      assessmentChange: { ids: string[] } | null;
      scenarioChange?: ScenarioSettingsUpdate | null;
    }) => {
      const savedSteps: string[] = [];
      let failedStep = "Team details";
      try {
        const team = await api.updateTeam(id, body);
        savedSteps.push("Team details");
        if (assessmentChange) {
          failedStep = "Assessment assignments";
          await api.setTeamAssessments(id, assessmentChange.ids);
          savedSteps.push("assessment assignments");
        }
        if (scenarioChange) {
          failedStep = "Scenario settings";
          await api.saveScenarioSettings(id, scenarioChange);
        }
        return team;
      } catch (err) {
        if (savedSteps.length === 0) throw err;
        const message = (err as { message?: string } | null)?.message || "Could not save.";
        throw new Error(
          `${savedSteps.join(" and ")} were saved. ${failedStep} were not saved: ${message} ` +
          "Your unsaved changes are still in the editor.",
          { cause: err },
        );
      }
    },
    onMutate: () => {
      // A retry attempt takes ownership of the editor; clear the
      // sticky post-create message so it doesn't sit next to a fresh
      // updateM error or a "Saving…" button.
      setPostCreateAssessmentError(null);
    },
    onError: (_err, vars) => Promise.all([
      // The ordinary team/assessment writes may have committed before a
      // later policy write failed. Refresh their saved state without
      // replacing the editor's local policy draft or original revision.
      qc.invalidateQueries({ queryKey: ["teams"] }),
      qc.invalidateQueries({ queryKey: ["team", vars.id, "assessments"] }),
      qc.invalidateQueries({ queryKey: ["assessments"] }),
      qc.invalidateQueries({ queryKey: ["scenario-settings", vars.id] }),
      qc.invalidateQueries({ queryKey: ["scenario-pool"] }),
      qc.invalidateQueries({ queryKey: ["scenario-run"] }),
    ]),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["teams"] });
      qc.invalidateQueries({ queryKey: ["scenario-settings", vars.id] });
      qc.invalidateQueries({ queryKey: ["scenario-pool"] });
      qc.invalidateQueries({ queryKey: ["scenario-run"] });
      if (vars.assessmentChange) {
        // Refresh the per-team cache the editor reads from AND the
        // supadmin assessments list (which shows team counts).
        qc.invalidateQueries({ queryKey: ["team", vars.id, "assessments"] });
        qc.invalidateQueries({ queryKey: ["assessments"] });
      }
      setEditing(null);
      setScrollToAssessments(false);
    },
  });
  const deleteM = useMutation({
    mutationFn: (id: string) => api.deleteTeam(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["teams"] }),
  });

  // Header cells in row order. The loading / empty rows take their colSpan
  // from this list, so they can't drift from the header again (they were
  // 7 on an 8-column table before Plan 31 added the Scenarios column).
  const teamColumns: readonly {
    key: string;
    label: string;
    align: "left" | "right";
  }[] = [
    { key: "team", label: "Team", align: "left" },
    { key: "community", label: "Community", align: "left" },
    { key: "agents", label: "Agents", align: "right" },
    { key: "calls", label: "Calls", align: "right" },
    { key: "assessment-count", label: "Assessments", align: "right" },
    { key: "managers", label: "Managers", align: "left" },
    { key: "assessments", label: "Assessments", align: "left" },
    { key: "scenarios", label: "Scenarios", align: "left" },
    { key: "actions", label: "", align: "right" },
  ];

  const noScope = (scopeQ.data ?? []).length === 0;
  const noScopeTitle = isSupadmin
    ? "No Communities exist yet — create one in the Communities page first"
    : "You are not assigned to any active Community";

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Manage Teams</h2>
          <p className="text-sm text-muted-foreground">
            Slice your Communities into Teams by placing agents and assigning
            Managers. Managers see only the calls in their Teams.
          </p>
        </div>
        <button
          type="button"
          disabled={noScope}
          title={noScope ? noScopeTitle : undefined}
          className="rounded bg-primary text-primary-foreground text-sm px-3 py-1.5 hover:bg-primary/90 disabled:opacity-50 transition-colors duration-[var(--duration-base)] ease-[var(--easing-standard)]"
          onClick={() => setEditing("new")}
        >
          New team
        </button>
      </div>

      {noScope && (
        <div className="rounded border border-accent/40 bg-accent/10 p-3 text-sm text-accent-foreground">
          {isSupadmin ? (
            <>
              No Communities exist yet. Head to the{" "}
              <span className="font-medium">Communities</span> page to create one,
              then come back here to slice it into Teams.
            </>
          ) : (
            <>
              You haven't been assigned to any active Communities yet. Ask a
              SupaAdmin to add you to a Community before creating Teams.
            </>
          )}
        </div>
      )}

      {editing && (
        <TeamEditor
          isSupadmin={isSupadmin}
          team={editing === "new" ? null : editing}
          scopedCommunities={scopeQ.data ?? []}
          managerCandidates={managerCandidates}
          dialpadUsers={dpUsersQ.data?.rows ?? []}
          agentsInOtherTeams={agentsInOtherTeams}
          duplicateAgentIds={duplicateAgentIds}
          scrollToAssessments={editing !== "new" && scrollToAssessments}
          onCancel={() => {
            setEditing(null);
            setScrollToAssessments(false);
            setPostCreateAssessmentError(null);
          }}
          onSubmit={(body, extras) => {
            if (editing === "new")
              createM.mutate({
                body,
                assessmentChange: extras.assessmentChange,
              });
            else
              updateM.mutate({
                id: editing.id,
                body,
                assessmentChange: extras.assessmentChange,
                scenarioChange: extras.scenarioChange,
              });
          }}
          submitting={createM.isPending || updateM.isPending}
          error={(updateM.error as { message?: string } | null)?.message
            || (createM.error as { message?: string } | null)?.message
            || postCreateAssessmentError
            || undefined}
        />
      )}

      <section className="rounded-lg border border-border bg-card shadow-md animate-card-enter">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/10 text-muted-foreground text-xs uppercase">
              <tr>
                {teamColumns.map((c) => (
                  <th
                    key={c.key}
                    className={`${c.align === "right" ? "text-right" : "text-left"} px-3 py-2`}
                  >
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(teamsQ.data ?? []).map((s) => (
                <tr key={s.id} className="border-t border-border hover:bg-primary/5 transition-colors duration-[var(--duration-base)] ease-[var(--easing-standard)]">
                  <td className="px-3 py-2 font-medium">
                    {teamHasDuplicateAgent(s) ? (
                      <span
                        className="text-destructive"
                        title="Has an agent who is also on another team"
                      >
                        {s.name}
                      </span>
                    ) : (
                      s.name
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{s.community_name}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{s.agent_count}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{s.call_count}</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    <button
                      type="button"
                      onClick={() => openEditor(s, true)}
                      className="text-foreground hover:underline"
                      title="Edit assessments assigned to this team"
                    >
                      {s.assessment_count}
                    </button>
                  </td>
                  <td className="px-3 py-2 align-top">
                    {s.manager_user_ids.length === 0 ? (
                      <span className="text-muted-foreground">—</span>
                    ) : (
                      <div className="flex flex-wrap gap-x-1 gap-y-0.5 max-w-[24rem]">
                        {s.manager_user_ids.map((mid, idx) => {
                          const u = (usersQ.data ?? []).find((x) => x.id === mid);
                          const name = u
                            ? (u.full_name?.trim()
                              || [u.first_name, u.last_name].filter(Boolean).join(" ").trim()
                              || u.email)
                            : `User ${mid.slice(0, 8)}`;
                          return (
                            <span key={mid} className="break-words">
                              {name}{idx < s.manager_user_ids.length - 1 ? "," : ""}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 align-top">
                    {s.assessments.length === 0 ? (
                      <span className="text-muted-foreground">—</span>
                    ) : (
                      <div className="flex flex-wrap gap-1 max-w-[24rem]">
                        {s.assessments.map((a) => (
                          <span
                            key={a.id}
                            className="inline-flex rounded-full bg-muted border border-border px-2 py-0.5 text-xs text-foreground"
                            title={a.name}
                          >
                            {a.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 align-top">
                    <ScenarioAvailability team={s} />
                  </td>
                  <td className="px-3 py-2 text-right text-xs">
                    <button
                      type="button"
                      onClick={() => openEditor(s)}
                      className="text-foreground hover:underline mr-3"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={async () => {
                        const ok = await dialog.confirm({
                          title: "Delete team?",
                          message: `Delete team "${s.name}"?`,
                          confirmLabel: "Delete",
                          destructive: true,
                        });
                        if (ok) deleteM.mutate(s.id);
                      }}
                      className="text-destructive hover:underline"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
              {teamsQ.isLoading ? (
                <tr><td colSpan={teamColumns.length} className="px-3 py-6 text-center text-sm text-muted-foreground">Loading…</td></tr>
              ) : (teamsQ.data?.length ?? 0) === 0 && (
                <tr><td colSpan={teamColumns.length} className="px-3 py-6 text-center text-sm text-muted-foreground">No teams yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {(scopeQ.data ?? [])
        .filter((g) => g.status === "active")
        .map((g) => (
          <UnassignedCallsSection key={g.id} communityId={g.id} communityName={g.name} />
        ))}
      {dialog.element}
    </div>
  );
}

function UnassignedCallsSection({
  communityId,
  communityName,
}: {
  communityId: string;
  communityName: string;
}) {
  const q = useQuery<UnassignedCall[]>({
    queryKey: ["unassigned-calls", communityId],
    queryFn: () => api.unassignedCalls(communityId),
    refetchInterval: false,
  });
  const rows = q.data ?? [];
  return (
    <section className="rounded-lg border border-border bg-card shadow-md animate-card-enter">
      <header className="px-4 py-3 border-b border-border flex items-baseline justify-between">
        <div>
          <h3 className="text-sm font-semibold text-foreground">
            Unassigned calls — {communityName}
          </h3>
          <p className="text-xs text-muted-foreground">
            Calls in this active Community that aren't yet placed in any Team.
          </p>
        </div>
        <span className="text-xs tabular-nums text-muted-foreground">
          {q.isLoading ? "…" : rows.length}
        </span>
      </header>
      {rows.length === 0 ? (
        <div className="px-4 py-6 text-sm text-muted-foreground text-center">
          {q.isLoading ? "Loading…" : "All calls are placed in a Team."}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/10 text-muted-foreground text-xs uppercase">
              <tr>
                <th className="text-left px-3 py-2">Started</th>
                <th className="text-left px-3 py-2">Agent</th>
                <th className="text-left px-3 py-2">Direction</th>
                <th className="text-left px-3 py-2">Call ID</th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 50).map((c) => (
                <tr key={c.call_id} className="border-t border-border hover:bg-primary/5 transition-colors duration-[var(--duration-base)] ease-[var(--easing-standard)]">
                  <td className="px-3 py-2 text-xs text-foreground">
                    {c.started_at ? new Date(c.started_at).toLocaleString() : "—"}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {c.agent_name || c.agent_id || (
                      <span className="text-muted-foreground">unidentified</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {c.direction || "—"}
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground font-mono">
                    {c.call_id}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length > 50 && (
            <div className="px-4 py-2 text-xs text-muted-foreground border-t border-border">
              Showing first 50 of {rows.length}.
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function TeamEditor({
  team,
  isSupadmin,
  scopedCommunities,
  managerCandidates,
  dialpadUsers,
  agentsInOtherTeams,
  duplicateAgentIds,
  scrollToAssessments = false,
  onCancel,
  onSubmit,
  submitting,
  error,
}: {
  team: TeamOut | null;
  isSupadmin: boolean;
  scopedCommunities: AdminScopedCommunity[];
  managerCandidates: UserOut[];
  dialpadUsers: DialpadUserRow[];
  agentsInOtherTeams: Set<string>;
  duplicateAgentIds: Set<string>;
  scrollToAssessments?: boolean;
  onCancel: () => void;
  onSubmit: (
    body: TeamIn,
    extras: {
      assessmentChange: { ids: string[] } | null;
      scenarioChange?: ScenarioSettingsUpdate | null;
    },
  ) => void;
  submitting: boolean;
  error?: string;
}) {
  const dialog = useConfirmDialog();
  const assessmentsRef = useRef<HTMLDivElement | null>(null);
  const didScrollRef = useRef(false);
  const teamId = team?.id ?? null;
  const configQ = useQuery({
    queryKey: ["scenario-settings", teamId],
    queryFn: () => api.scenarioSettings(teamId!),
    enabled: !!teamId,
    placeholderData: undefined,
    staleTime: 0,
  });
  const [scenarioDraft, setScenarioDraft] = useState<ScenarioSettingsUpdate | null>(null);
  const [scenariosExpanded, setScenariosExpanded] = useState(false);
  useEffect(() => {
    setScenariosExpanded(false);
    setScenarioDraft(null);
  }, [teamId]);
  const scenarioValue = scenarioDraft ?? configQ.data;
  const scenarioDirty = scenarioDraft !== null && (
    scenarioDraft.enabled !== configQ.data?.enabled ||
    JSON.stringify(scenarioDraft.settings) !== JSON.stringify(configQ.data?.settings)
  );
  const currentAssessmentsQ = useQuery<AssessmentTeamRef[]>({
    queryKey: ["team", teamId, "assessments"],
    queryFn: () =>
      api.getTeamAssessments(teamId as string),
    enabled: !!teamId,
  });
  // Assignable list is the same set of supadmin-saved assessments
  // regardless of which team we're editing, so it's safe (and useful)
  // to load it for "New team" too — that's the whole point of ticket 31.
  const assignableQ = useQuery<AssignableAssessment[]>({
    queryKey: ["assessments", "assignable"],
    queryFn: () => api.listAssignableAssessments(),
  });
  // Initial selection — what's stored server-side right now. For a
  // brand-new team there's nothing stored yet, so the baseline is an
  // empty list and any pick is "dirty".
  const initialAssessmentIds = useMemo(
    () =>
      [...((currentAssessmentsQ.data ?? []).map((a) => a.id))].sort(),
    [currentAssessmentsQ.data],
  );
  const [assessmentIds, setAssessmentIds] = useState<string[]>([]);
  const [assessmentsHydrated, setAssessmentsHydrated] = useState(!teamId);
  useEffect(() => {
    if (!assessmentsHydrated && currentAssessmentsQ.data) {
      setAssessmentIds(initialAssessmentIds);
      setAssessmentsHydrated(true);
    }
  }, [assessmentsHydrated, currentAssessmentsQ.data, initialAssessmentIds]);

  // Scroll the Assessments section into view once it's rendered with
  // real data, but only when the editor was opened via the Teams-list
  // assessment-count cell. Guarded so re-renders don't keep scrolling.
  useEffect(() => {
    if (
      scrollToAssessments
      && !didScrollRef.current
      && assessmentsHydrated
      && assessmentsRef.current
    ) {
      didScrollRef.current = true;
      assessmentsRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [scrollToAssessments, assessmentsHydrated]);

  const assessmentsDirty = useMemo(() => {
    const a = [...assessmentIds].sort();
    if (a.length !== initialAssessmentIds.length) return true;
    return a.some((id, i) => id !== initialAssessmentIds[i]);
  }, [assessmentIds, initialAssessmentIds]);
  const [communityId, setCommunityId] = useState(
    team?.community_id ?? scopedCommunities[0]?.id ?? "",
  );
  const [name, setName] = useState(team?.name ?? "");
  const [agentIds, setAgentIds] = useState<string[]>(
    team?.dialpad_user_ids ?? [],
  );
  const [managerIds, setManagerIds] = useState<string[]>(
    team?.manager_user_ids ?? [],
  );

  const submit = () => {
    onSubmit(
      {
        community_id: communityId,
        name: name.trim(),
        manager_user_ids: managerIds,
        dialpad_user_ids: agentIds,
      },
      {
        // Only ask the parent to fire the assessments PUT when the
        // user actually changed the selection — keeps the call cheap
        // and avoids writing an empty change-log row. For "New team"
        // the baseline is the empty list, so any pick counts as dirty
        // and the parent's create flow will chain the PUT.
        assessmentChange: assessmentsDirty ? { ids: assessmentIds } : null,
        scenarioChange: isSupadmin && scenarioDirty && scenarioValue ? {
          revision: scenarioValue.revision,
          enabled: scenarioValue.enabled,
          settings: scenarioValue.settings,
        } : null,
      },
    );
  };

  // Cancel guard: the Assessments picker is now available before the
  // team exists (ticket 31), so a Cancel on "New team" silently throws
  // away the user's picks with no server-side trace. Warn before
  // discarding. Edit Team gets the same guard for parity.
  const requestCancel = async () => {
    if (assessmentsDirty || scenarioDirty) {
      const ok = await dialog.confirm({
        title: "Discard unsaved changes?",
        message: team
          ? "Discard your unsaved assessment or scenario changes for this team?"
          : "Discard the assessments you picked for this new team?",
        confirmLabel: "Discard",
        destructive: true,
      });
      if (!ok) return;
    }
    onCancel();
  };

  const parentCommunity = useMemo(
    () => scopedCommunities.find((g) => g.id === communityId),
    [scopedCommunities, communityId],
  );

  // The agent pool eligible for the selected Community — the single source
  // of truth for which agents this Team may contain. Mirrors the backend
  // membership rule in services/group_resolution.py. The picker must only
  // ever OFFER agents from this pool; feeding it the full Dialpad directory
  // leaks agents from other Communities into the add-dropdown.
  const eligibleAgents = useMemo(
    () => computeEligibleAgents(parentCommunity, dialpadUsers),
    [parentCommunity, dialpadUsers],
  );

  // Options handed to the picker: the eligible pool, PLUS any already-selected
  // agent that is no longer eligible (e.g. selected before a brand change, or
  // retained from legacy data) so its chip still resolves to a real name and
  // stays removable. Those stragglers are added to `notOfferable` below so
  // removing one cannot re-add it from the dropdown.
  const pickerOptions = useMemo(() => {
    const byId = new Map(eligibleAgents.map((u) => [u.id, u]));
    for (const id of agentIds) {
      if (byId.has(id)) continue;
      const u = dialpadUsers.find((d) => d.id === id);
      if (u) byId.set(id, u);
    }
    return Array.from(byId.values());
  }, [eligibleAgents, agentIds, dialpadUsers]);

  // Ids never offered as add-options in the dropdown: agents already on
  // another team (PET-665) and any currently-selected agent outside this
  // Community's eligible pool. Both stay visible + removable as chips
  // because they remain in `pickerOptions`.
  const notOfferable = useMemo(() => {
    const set = new Set(agentsInOtherTeams);
    const eligibleIds = new Set(eligibleAgents.map((u) => u.id));
    for (const id of agentIds) {
      if (!eligibleIds.has(id)) set.add(id);
    }
    return set;
  }, [agentsInOtherTeams, eligibleAgents, agentIds]);

  // Resolved member set previewed in the "Show agents in this team" panel:
  // the selected agents that actually qualify for the parent Community.
  // Built from the same `eligibleAgents` pool so the preview and the picker
  // can never disagree. An include-exceptioned agent whose Dialpad record
  // isn't loaded is kept as an id-only row (it qualifies; we just have no
  // name to show). Note: agents the team also pulls in via legacy Dialpad-
  // group assignments retained server-side are intentionally not previewed
  // here — those are no longer editable in the UI, but the server-side
  // `agent_count` on the Teams list still reflects them.
  const resolvedMembers = useMemo(() => {
    const extraSet = new Set(agentIds);
    const seen = new Map<string, DialpadUserRow | null>();
    for (const u of eligibleAgents) {
      if (extraSet.has(u.id)) seen.set(u.id, u);
    }
    // include/exclude are mutually exclusive per agent (community_agent_
    // exceptions PK), so an include id that isn't a loaded Dialpad user
    // still qualifies — render it as an id-only row.
    const includeSet = new Set(
      (parentCommunity?.exceptions ?? [])
        .filter((e) => e.mode === "include")
        .map((e) => e.dialpad_user_id),
    );
    for (const id of extraSet) {
      if (!seen.has(id) && includeSet.has(id)) seen.set(id, null);
    }
    const rows = Array.from(seen.entries()).map(([id, u]) => ({
      id,
      name: (u?.display_name || "").trim(),
      email: (u?.email || "").trim(),
    }));
    rows.sort((a, b) => {
      const an = (a.name || a.email || a.id).toLowerCase();
      const bn = (b.name || b.email || b.id).toLowerCase();
      return an.localeCompare(bn);
    });
    return rows;
  }, [eligibleAgents, agentIds, parentCommunity]);

  return (
    <div className="rounded-lg border border-input bg-card p-4 shadow-md space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">{team ? `Edit team: ${team.name}` : "New team"}</h3>
        <button type="button" onClick={requestCancel} className="text-sm text-muted-foreground hover:text-foreground">
          Cancel
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="text-xs text-muted-foreground">
          Community
          <select
            value={communityId}
            onChange={(e) => setCommunityId(e.target.value)}
            disabled={!!team}
            className="mt-1 w-full border border-input rounded px-2 py-1.5 text-sm disabled:bg-muted"
          >
            {scopedCommunities.map((g) => (
              <option key={g.id} value={g.id}>{g.name}</option>
            ))}
          </select>
        </label>
        <label className="text-xs text-muted-foreground">
          Name
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 w-full border border-input rounded px-2 py-1.5 text-sm"
          />
        </label>
      </div>
      <div className="space-y-1">
        <div className="text-xs text-muted-foreground">
          Agents <span className="text-muted-foreground">— pick the agents who belong to this team (only agents in this Community are offered)</span>
        </div>
        <DialpadAgentPicker
          value={agentIds}
          onChange={setAgentIds}
          options={pickerOptions}
          excludeIds={notOfferable}
          duplicateIds={duplicateAgentIds}
          placeholder="No extra agents"
        />
      </div>
      <div>
        <div className="text-xs text-muted-foreground mb-1">Managers assigned</div>
        <div className="max-h-40 overflow-y-auto border border-border rounded p-2 space-y-1">
          {managerCandidates.length === 0 ? (
            <div className="text-xs text-muted-foreground">No managers available — invite one from Manage Users.</div>
          ) : managerCandidates.map((u) => (
            <label key={u.id} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={managerIds.includes(u.id)}
                onChange={(e) => {
                  setManagerIds((prev) =>
                    e.target.checked ? [...prev, u.id] : prev.filter((x) => x !== u.id),
                  );
                }}
              />
              <span>{u.full_name || u.email}</span>
            </label>
          ))}
        </div>
      </div>
      {dialpadUsers.length > 0 && (
        <details className="text-xs text-muted-foreground">
          <summary className="cursor-pointer">
            Show agents in this team ({resolvedMembers.length})
          </summary>
          <div className="mt-2 max-h-40 overflow-y-auto text-xs border border-border rounded">
            {resolvedMembers.length === 0 ? (
              <div className="px-2 py-1.5 italic text-muted-foreground">
                No agents match this selection yet.
              </div>
            ) : (
              resolvedMembers.map((m) => (
                <div
                  key={m.id}
                  className="px-2 py-1 border-b border-border last:border-b-0"
                >
                  <span className="text-foreground">
                    {m.name || m.email || m.id}
                  </span>
                  {m.name && m.email && (
                    <span className="text-muted-foreground"> · {m.email}</span>
                  )}
                </div>
              ))
            )}
          </div>
        </details>
      )}
      <div ref={assessmentsRef} className="space-y-1 scroll-mt-4">
        <div className="text-xs text-muted-foreground uppercase tracking-wide">
          Assessments
        </div>
        {(teamId && !currentAssessmentsQ.data) || !assignableQ.data ? (
          <div className="text-xs text-muted-foreground">Loading assessments…</div>
        ) : (assignableQ.data ?? []).length === 0 ? (
          <div className="rounded border border-accent/40 bg-accent/10 p-3 text-sm text-accent-foreground">
            No assessments available to assign. Ask a Super Admin to create one.
          </div>
        ) : (
          <div className="flex flex-col gap-1 max-h-64 overflow-y-auto rounded border border-input p-2 bg-muted/30">
            {assignableQ.data.map((a) => (
              <label
                key={a.id}
                className="w-full flex items-center gap-2 px-2 py-1 text-sm hover:bg-muted/40"
              >
                <input
                  type="checkbox"
                  checked={assessmentIds.includes(a.id)}
                  onChange={(e) => {
                    setAssessmentIds((prev) =>
                      e.target.checked
                        ? [...prev, a.id]
                        : prev.filter((x) => x !== a.id),
                    );
                  }}
                />
                <span className="flex-1 truncate">
                  <span className="font-medium text-foreground">{a.name}</span>
                  {a.description && (
                    <span className="text-xs text-muted-foreground"> · {a.description}</span>
                  )}
                </span>
              </label>
            ))}
          </div>
        )}
      </div>
      {teamId && (
        <section>
          <button
            type="button"
            aria-expanded={scenariosExpanded}
            aria-controls="team-scenario-settings"
            className="hover-elevate flex w-full items-center gap-2 rounded border border-border px-4 py-3 text-left text-sm font-semibold"
            onClick={() => setScenariosExpanded((expanded) => !expanded)}
          >
            <span aria-hidden="true">{scenariosExpanded ? "▾" : "▸"}</span>
            Scenarios
          </button>
          {configQ.isLoading && <p role="status" className="text-sm text-muted-foreground">Loading scenario settings…</p>}
          {configQ.isError && (
            <p role="alert" className="text-sm text-destructive">
              Could not load scenario settings.{" "}
              <button type="button" className="hover-elevate rounded px-2 py-1 underline" onClick={() => void configQ.refetch()}>Retry</button>
            </p>
          )}
          <div id="team-scenario-settings" hidden={!scenariosExpanded}>
            {scenariosExpanded && !isSupadmin && (
              <p className="py-2 text-xs text-muted-foreground">
                Read only. Only super admins can change scenario settings.
              </p>
            )}
            {scenariosExpanded && configQ.data && scenarioValue && (
              <TeamScenarioFields
                value={scenarioValue}
                data={configQ.data}
                onChange={(next) => { if (isSupadmin) setScenarioDraft(next); }}
                disabled={submitting || !isSupadmin}
              />
            )}
          </div>
        </section>
      )}
      {error && <div role="alert" className="text-xs text-destructive">{error}</div>}
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={submit}
          disabled={submitting || !name.trim() || !communityId || (isSupadmin && !!teamId && !configQ.data)}
          className="rounded bg-primary text-primary-foreground text-sm px-3 py-1.5 hover:bg-primary/90 disabled:opacity-50 transition-colors duration-[var(--duration-base)] ease-[var(--easing-standard)]"
        >
          {submitting ? "Saving…" : "Save"}
        </button>
      </div>
      {dialog.element}
    </div>
  );
}
